import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { FALLBACK_SUBACCOUNT_RANGE, getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getCuratorName } from '@/features/elend/logic/useELend'
import { type VaultMarket, getVaultMarket } from '@/features/evault-details/data/products'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { Address, formatUnits } from 'viem'
import { useAccount, useReadContracts } from 'wagmi'
import { useELendsList } from '../elends/useELendInfo'

export interface ELendPosition {
  token: Token
  vaultAddress: Address
  vaultName: string
  vaultSymbol: string
  depositAmount: NormalizedUnitNumber
  depositAmountUSD: NormalizedUnitNumber
  supplyAPY: Percentage
  chainId: number
  rowClickOptions: { destination: string }
  curatorName?: string
  market?: VaultMarket
  /** Sub-account ID (0 = main account, 1-255 = sub-accounts) */
  subAccountId: number
  /** The sub-account address for this position */
  subAccountAddress: Address
}

export interface UseELendPositionsParams {
  userAddress?: Address
  chainId?: number
  enabled?: boolean
}

export interface UseELendPositionsResult {
  positions: ELendPosition[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to fetch eLend positions from onchain
 * Fetches user deposits in Euler lending vaults
 */
export function useELendPositions({
  userAddress,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseELendPositionsParams = {}): UseELendPositionsResult {
  const { address: connectedAddress } = useAccount()
  const address = userAddress || connectedAddress

  // Get active subaccount IDs from the global discovery cache
  const { activeSubaccountIds, isDiscovering } = useSubaccountStore()

  // Step 1: Get all eLend vaults with their metadata (respects SHOW_ALL_ELEND_VAULTS)
  // This already has name, symbol, asset, APY, etc. - no need to fetch again!
  const { vaults: elendVaults, isLoading: isLoadingVaults } = useELendsList(chainId)

  // Generate sub-account addresses to query
  // Include fallback range (0-15) for safety, plus any higher active subaccounts from discovery
  const subAccountAddresses = useMemo(() => {
    if (!address) return []
    // Create a unique set of IDs: fallback range + discovered active IDs
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    const idsToQuery = [...new Set([...fallbackIds, ...activeSubaccountIds])]
    return idsToQuery.map((id) => ({
      subAccountId: id,
      subAccountAddress: getSubAccountAddress(address, id),
    }))
  }, [address, activeSubaccountIds])

  // Step 2: Fetch user balances for ALL sub-accounts and share prices for each vault
  // Structure: [vault0_sharePrice, vault0_subAccount0_balance, vault0_subAccount1_balance, ..., vault1_sharePrice, ...]
  const userBalanceContracts = useMemo(() => {
    if (!address || !enabled) return []
    return elendVaults.flatMap((vault) => [
      // First, get share price for this vault (only once per vault)
      {
        address: vault.address,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets' as const,
        args: [BigInt(10 ** 18)] as const,
        chainId,
      },
      // Then, get balance for each sub-account
      ...subAccountAddresses.map(({ subAccountAddress }) => ({
        address: vault.address,
        abi: eVaultImplementationAbi,
        functionName: 'balanceOf' as const,
        args: [subAccountAddress] as const,
        chainId,
      })),
    ])
  }, [elendVaults, address, chainId, enabled, subAccountAddresses])

  const { data: balanceData, isLoading: isLoadingBalances } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: userBalanceContracts as any,
    query: {
      enabled: enabled && !!address && elendVaults.length > 0,
      staleTime: 30_000,
    },
  })

  // Step 3: Build positions by combining vault metadata with user balances for each sub-account
  const positions: ELendPosition[] = useMemo(() => {
    if (!address || !balanceData) return []

    const result: ELendPosition[] = []
    // Each vault has 1 sharePrice + N subaccount balances
    const dataPerVault = 1 + subAccountAddresses.length

    for (let vaultIdx = 0; vaultIdx < elendVaults.length; vaultIdx++) {
      const vault = elendVaults[vaultIdx]
      if (!vault || !vault.asset) continue

      // Get share price for this vault (first entry for this vault)
      const vaultDataStart = vaultIdx * dataPerVault
      const sharePrice = (balanceData[vaultDataStart]?.result as bigint) || BigInt(10 ** 18)

      // Use asset info from vault data
      const tokenSymbol = vault.assetSymbol || 'UNKNOWN'
      const tokenDecimals = vault.assetDecimals || 18

      const token = new Token({
        symbol: TokenSymbol(tokenSymbol),
        name: tokenSymbol,
        decimals: tokenDecimals,
        address: CheckedAddress(vault.asset),
        unitPriceUsd: vault.assetPrice?.toString() || '0',
        logoURI: undefined, // Token icons are handled by TokenIcon component
      })

      // Use totalApy from indexer, or default to 0 if not available
      const supplyAPY = vault.totalApy ?? 0

      // Get curator name and market info
      const curatorName = getCuratorName(vault)
      const market = getVaultMarket(vault.address, vault.products)

      // Check each sub-account for balances
      for (let subIdx = 0; subIdx < subAccountAddresses.length; subIdx++) {
        const { subAccountId, subAccountAddress } = subAccountAddresses[subIdx]!
        // Balance is at: vaultDataStart + 1 (skip sharePrice) + subIdx
        const balanceIndex = vaultDataStart + 1 + subIdx
        const userBalance = (balanceData[balanceIndex]?.result as bigint) || 0n

        // Skip if no balance for this sub-account
        if (userBalance === 0n) continue

        // Calculate deposit amount
        const depositAssets = (userBalance * sharePrice) / BigInt(10 ** 18)
        // Use formatUnits directly without Number() to preserve precision for large values
        const depositAmountStr = formatUnits(depositAssets, tokenDecimals)

        result.push({
          token,
          vaultAddress: vault.address,
          vaultName: vault.name,
          vaultSymbol: vault.symbol,
          depositAmount: NormalizedUnitNumber(depositAmountStr),
          depositAmountUSD: NormalizedUnitNumber(
            vault.assetPrice ? new BigNumber(depositAmountStr).times(vault.assetPrice).toString() : '0',
          ),
          supplyAPY: Percentage(supplyAPY / 100, true, true),
          chainId,
          curatorName,
          market,
          subAccountId,
          subAccountAddress,
          rowClickOptions: {
            destination: `/markets/elend/${chainId}/${vault.address}?subAccountId=${subAccountId}`,
          },
        })
      }
    }

    return result
  }, [elendVaults, balanceData, address, chainId, subAccountAddresses])

  // Simple loading check: vaults + user balances + discovery
  const isLoading = isLoadingVaults || (!!address && isLoadingBalances) || isDiscovering

  return {
    positions,
    isLoading,
    error: null,
  }
}
